import type { KnowledgeArticle, KnowledgeSection, Locale } from "../content/types"
import Breadcrumbs from "../components/Breadcrumbs"
import PageCta from "../components/PageCta"
import JsonLd from "../components/seo/JsonLd"
import { RelatedCases, RelatedServices } from "../components/RelatedContent"
import { buildArticleSchema } from "../seo/schema/article"
import { knowledgeIndexContent, getReadingTimeMinutes } from "../content/knowledge/index"

interface KnowledgeArticleTemplateProps {
  article: KnowledgeArticle
  locale: Locale
}

function Section({ section }: { section: KnowledgeSection }) {
  switch (section.type) {
    case "heading":
      return <h2 className="text-2xl md:text-3xl font-bold text-text-primary mt-12 mb-4">{section.text}</h2>
    case "paragraph":
      return <p className="text-lg text-text-secondary leading-relaxed mb-5">{section.text}</p>
    case "list":
      return (
        <ul className="list-none space-y-3 my-6">
          {section.items.map((item) => (
            <li key={item} className="flex items-start gap-3 text-text-secondary leading-relaxed">
              <span className="w-2 h-2 rounded-full bg-button-primary mt-2.5 flex-shrink-0" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )
    case "callout":
      return (
        <aside className="bg-button-primary/5 border border-button-primary/20 rounded-xl p-6 my-8">
          {section.title && <p className="font-bold text-button-primary mb-2">{section.title}</p>}
          <p className="text-text-secondary leading-relaxed">{section.text}</p>
        </aside>
      )
  }
}

export default function KnowledgeArticleTemplate({ article, locale }: KnowledgeArticleTemplateProps) {
  const indexTitle = knowledgeIndexContent[locale === "nl" ? "nl" : "en"].title
  const indexPath = locale === "nl" ? "/nl/kennis/" : "/en/insights/"
  const dateFmt = new Intl.DateTimeFormat(locale === "nl" ? "nl-NL" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  const schema = buildArticleSchema(article, locale)

  return (
    <article className="min-h-screen bg-site-bg">
      {schema && <JsonLd data={schema} />}
      <div className="max-w-3xl mx-auto px-4 pt-12 pb-24">
        <Breadcrumbs
          locale={locale}
          items={[{ label: indexTitle, path: indexPath }, { label: article.h1?.[locale] ?? "" }]}
        />

        <header className="mt-8 mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">{article.h1?.[locale]}</h1>
          <p className="text-xl text-text-secondary leading-relaxed">{article.description[locale]}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-text-muted">
            <span>{article.author}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={article.published}>{dateFmt.format(new Date(article.published))}</time>
            <span aria-hidden="true">·</span>
            <span>
              {getReadingTimeMinutes(article)} min {locale === "nl" ? "leestijd" : "read"}
            </span>
          </div>
        </header>

        <div>
          {article.sections.map((section, i) => (
            <Section key={i} section={section} />
          ))}
        </div>

        <RelatedServices locale={locale} ids={article.relatedServiceIds} />
        <RelatedCases locale={locale} ids={article.relatedCaseIds} />
        <PageCta locale={locale} />
      </div>
    </article>
  )
}
